import React from 'react';
import {
  ConfigFieldDef,
  COMPUTE_MULTIPLIER_ROWS,
  DIFFICULTY_MULTIPLIER_ROWS,
  ModuleConfigTabDef,
} from './moduleConfigDefs';

interface ModuleConfigTabProps {
  tab: ModuleConfigTabDef;
  values: Record<string, number | string>;
  multipliers: Record<string, number>;
  computeMultipliers: Record<string, number>;
  onFieldChange: (key: string, value: string) => void;
  onMultiplierChange: (arrivalType: string, value: string) => void;
  onComputeMultiplierChange: (arrivalType: string, value: string) => void;
}

const cleanNumeric = (value: string): string => value.replace(/^0+(?=\d)/, '');

const SelectField: React.FC<{
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
}> = ({ label, value, options, onChange }) => (
  <div className="space-y-2">
    <label className="block text-sm font-medium text-text-grey">{label}</label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="block w-full rounded-md border-[#E2E5EA] focus:border-hero-red focus:ring-hero-red sm:text-sm px-3 py-2"
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  </div>
);

const NumberField: React.FC<{
  label: string;
  value: number;
  onChange: (value: string) => void;
  symbol?: string;
  isPercentage?: boolean;
  suffix?: string;
  tooltip?: string;
}> = ({ label, value, onChange, symbol, isPercentage, suffix, tooltip }) => (
  <div className="space-y-2">
    <label className="block text-sm font-medium text-text-grey">
      {label}
      {tooltip && (
        <span
          className="ml-1 inline-flex h-4 w-4 items-center justify-center rounded-full bg-[#E8EAED] text-xs text-text-grey cursor-help"
          title={tooltip}
        >
          ?
        </span>
      )}
    </label>
    <div className="relative rounded-md shadow-sm">
      {symbol && (
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <span className="text-heritage-grey sm:text-sm">{symbol}</span>
        </div>
      )}
      <input
        type="number"
        aria-label={label}
        title={tooltip ?? label}
        value={value || ''}
        onChange={(e) => onChange(cleanNumeric(e.target.value))}
        className={`block w-full rounded-md border-[#E2E5EA] focus:border-hero-red focus:ring-hero-red sm:text-sm ${
          symbol ? 'pl-7' : 'pl-3'
        } ${isPercentage || suffix ? 'pr-12' : 'pr-3'} py-2`}
        min="0"
        step="any"
      />
      {(isPercentage || suffix) && (
        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
          <span className="text-heritage-grey sm:text-sm">{isPercentage ? '%' : suffix}</span>
        </div>
      )}
    </div>
  </div>
);

const ModuleConfigTab: React.FC<ModuleConfigTabProps> = ({
  tab,
  values,
  multipliers,
  computeMultipliers,
  onFieldChange,
  onMultiplierChange,
  onComputeMultiplierChange,
}) => (
  <div className="space-y-6">
    <div className="text-sm text-heritage-grey mb-4">{tab.description}</div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {tab.fields.map((field: ConfigFieldDef) =>
        field.type === 'select' ? (
          <SelectField
            key={field.key}
            label={field.label}
            value={String(values[field.key] ?? '')}
            options={field.options ?? []}
            onChange={(value) => onFieldChange(field.key, value)}
          />
        ) : (
          <NumberField
            key={field.key}
            label={field.label}
            value={Number(values[field.key] ?? 0)}
            onChange={(value) => onFieldChange(field.key, value)}
            symbol={field.symbol}
            isPercentage={field.isPercentage}
            suffix={field.suffix}
            tooltip={field.tooltip}
          />
        )
      )}
    </div>

    {tab.hasDifficultyMultipliers && (
      <div className="border-t pt-6 mt-6">
        <h3 className="text-lg font-medium text-blueprint-blue font-heading mb-4">
          Difficulty Multipliers
        </h3>
        <div className="text-sm text-heritage-grey mb-4">
          Effort multiplier applied per source arrival type. Harder sources
          (handwritten, scanned PDF) cost more to onboard.
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {DIFFICULTY_MULTIPLIER_ROWS.map((arrival) => (
            <NumberField
              key={arrival.value}
              label={arrival.label}
              value={multipliers[arrival.value] ?? 0}
              onChange={(value) => onMultiplierChange(arrival.value, value)}
              suffix="x"
            />
          ))}
        </div>
      </div>
    )}

    {tab.hasComputeMultipliers && (
      <div className="border-t pt-6 mt-6">
        <h3 className="text-lg font-medium text-blueprint-blue font-heading mb-4">
          Compute Multipliers
        </h3>
        <div className="text-sm text-heritage-grey mb-4">
          Extraction-compute multiplier applied per source arrival type, to the
          compute line only. Harder formats (scanned PDF, handwritten) cost more
          to process. Kept separate from the labour difficulty: API stays 1.0.
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {COMPUTE_MULTIPLIER_ROWS.map((arrival) => (
            <NumberField
              key={arrival.value}
              label={arrival.label}
              value={computeMultipliers[arrival.value] ?? 0}
              onChange={(value) => onComputeMultiplierChange(arrival.value, value)}
              suffix="x"
            />
          ))}
        </div>
      </div>
    )}
  </div>
);

export default ModuleConfigTab;
